import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { ESPESSURA_BARRA, LARGURA_NOMES, alturaGrafico, nomeCurto } from './graficoPadrao';

/** Tick do eixo de nomes numa linha só — o padrão do Recharts quebra o texto em 2 linhas. */
export function TickNome({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} className="fill-muted-foreground">
      {payload?.value}
    </text>
  );
}

export interface BarraClicavel {
  chave: string;
  rotulo: string;
  valor: number;
  cor: string;
}

export interface PessoaDaBarra {
  id: string;
  nome: string;
  /** Texto à direita (ex: "12", "há 3 dias", "2 de 6 módulos"). */
  detalhe: string;
  /** Esmaecida (ex: "nunca usou" na adoção). */
  apagada?: boolean;
}

/** Lista de pessoas de uma barra selecionada — nome leva à página da pessoa. */
export function ListaPessoas({
  titulo, pessoas, onFechar, linkPessoa,
}: {
  titulo: string;
  pessoas: PessoaDaBarra[];
  onFechar: () => void;
  linkPessoa: (id: string) => string;
}) {
  return (
    <div className="mt-2 rounded-md border">
      <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-1.5">
        <span className="text-sm font-medium">{titulo} ({pessoas.length})</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onFechar} title="Fechar">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {pessoas.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">Ninguém.</p>
      ) : (
        <ul className="max-h-64 divide-y overflow-y-auto">
          {pessoas.map((p) => (
            <li key={p.id} className={`flex items-center justify-between gap-3 px-3 py-1.5 text-sm ${p.apagada ? 'text-muted-foreground' : ''}`}>
              <Link to={linkPessoa(p.id)} className="truncate hover:underline">{p.nome}</Link>
              <span className="shrink-0 text-xs text-muted-foreground">{p.detalhe}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const config: ChartConfig = { valor: { label: 'Quantidade' } };

/** Gráfico de barras horizontais padrão; clicar numa barra abre embaixo quem está nela. */
export function GraficoBarrasClicavel({
  titulo, icone, descricao, barras, pessoasDa, linkPessoa, rotuloValor = 'Quantidade', formatarValor,
}: {
  titulo: string;
  icone: ReactNode;
  descricao?: string;
  barras: BarraClicavel[];
  pessoasDa: (chave: string) => PessoaDaBarra[];
  linkPessoa: (id: string) => string;
  rotuloValor?: string;
  formatarValor?: (v: number) => string;
}) {
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const barraSelecionada = barras.find((b) => b.chave === selecionada);
  const dados = barras.map((b) => ({ ...b, name: nomeCurto(b.rotulo) }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1.5 text-base font-medium">
          {icone}
          {titulo}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{descricao ?? 'Clique numa barra pra ver quem está nela.'}</p>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="aspect-auto w-full" style={{ height: alturaGrafico(dados.length) }}>
          <BarChart data={dados} layout="vertical" margin={{ left: 8, right: 24 }} barSize={ESPESSURA_BARRA}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
            <YAxis dataKey="name" type="category" interval={0} tick={<TickNome />} tickLine={false} axisLine={false} width={LARGURA_NOMES} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  hideIndicator
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.rotulo}
                  formatter={(v) => `${rotuloValor}: ${formatarValor ? formatarValor(Number(v)) : v}`}
                />
              }
            />
            <Bar
              dataKey="valor"
              radius={[0, 4, 4, 0]}
              className="cursor-pointer"
              onClick={(d: { chave?: string }) => d?.chave && setSelecionada((atual) => (atual === d.chave ? null : d.chave!))}
            >
              {dados.map((b) => (
                <Cell key={b.chave} fill={b.cor} fillOpacity={selecionada && selecionada !== b.chave ? 0.35 : 1} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
        {barraSelecionada && (
          <ListaPessoas
            titulo={barraSelecionada.rotulo}
            pessoas={pessoasDa(barraSelecionada.chave)}
            onFechar={() => setSelecionada(null)}
            linkPessoa={linkPessoa}
          />
        )}
      </CardContent>
    </Card>
  );
}
